import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Services.css';

const allServices = [
  {
    icon: '🤖',
    title: 'AI Agents',
    short: 'Autonomous agents that reason, plan and act.',
    desc: 'We design and deploy purpose-built AI agents using LLMs, RAG pipelines, and multi-agent frameworks. From customer service bots to complex business-process orchestration agents — we cover it all.',
    features: ['LLM-powered reasoning', 'Tool-use & API integration', 'Multi-agent orchestration', 'RAG & vector search', 'LangChain / LlamaIndex'],
    color: '#00d4d4',
  },
  {
    icon: '💬',
    title: 'Chatbot Development',
    short: 'Smart, context-aware bots for every channel.',
    desc: 'Build production-grade conversational AI for web, mobile, and messaging platforms. Custom-trained on your knowledge base with seamless CRM and helpdesk integrations.',
    features: ['Custom NLP fine-tuning', 'Omnichannel deployment', 'CRM & helpdesk integration', 'Analytics dashboard', 'Voice & text support'],
    color: '#0097a7',
  },
  {
    icon: '🌐',
    title: 'Web Applications',
    short: 'Scalable, beautiful, production-ready web apps.',
    desc: 'Full-stack MERN & PHP web applications built with performance, SEO, and scalability in mind. SaaS platforms, dashboards, e-commerce, portals — anything you can imagine.',
    features: ['React / Next.js frontends', 'Node.js & PHP backends', 'MongoDB & PostgreSQL', 'REST & GraphQL APIs', 'AWS / GCP / Azure hosting'],
    color: '#00e5ff',
  },
  {
    icon: '📱',
    title: 'Mobile Apps',
    short: 'iOS & Android apps with native performance.',
    desc: 'Cross-platform React Native apps that look and feel native. From consumer apps to enterprise mobility solutions with offline support, push notifications, and device integrations.',
    features: ['React Native (iOS & Android)', 'Offline-first architecture', 'Push notifications', 'Biometric auth', 'App Store & Play Store launch'],
    color: '#00b8d4',
  },
  {
    icon: '🏥',
    title: 'Healthcare Applications',
    short: 'HIPAA-compliant digital health solutions.',
    desc: 'Patient portals, telemedicine platforms, EHR integrations, and clinical decision-support tools — all built with HIPAA compliance, HL7/FHIR standards, and security at the core.',
    features: ['HIPAA & HITECH compliance', 'HL7 FHIR integration', 'Telemedicine features', 'EHR connectivity', 'AI diagnostics support'],
    color: '#00c8a0',
  },
  {
    icon: '🛡️',
    title: 'Insurance Solutions',
    short: 'End-to-end InsurTech platforms.',
    desc: 'Digital-first insurance platforms covering quoting, underwriting, policy management, claims processing, and regulatory compliance — purpose-built for carriers, MGAs, and brokers.',
    features: ['Policy lifecycle management', 'Claims automation', 'Regulatory compliance', 'API integrations', 'AI fraud detection'],
    color: '#4dd0e1',
  },
];

const cloudServices = [
  { icon: '☁️', name: 'AWS', desc: 'EC2, S3, Lambda, RDS, EKS, CloudFront, SageMaker' },
  { icon: '🔷', name: 'Microsoft Azure', desc: 'Azure AI, AKS, Cosmos DB, App Service, DevOps' },
  { icon: '🌩️', name: 'Google Cloud', desc: 'GKE, BigQuery, Vertex AI, Cloud Run, Firebase' },
];

const devOps = [
  { name: 'Docker', desc: 'Containerisation for every environment' },
  { name: 'Kubernetes', desc: 'Orchestration & auto-scaling at scale' },
  { name: 'Jenkins', desc: 'CI/CD pipelines & automated testing' },
  { name: 'Terraform', desc: 'Infrastructure as Code (IaC)' },
];

const Services = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('active'); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="services-page">
      {/* Hero */}
      <section className="page-hero">
        <div className="page-hero-bg"></div>
        <div className="section-wrapper page-hero-inner">
          <span className="section-tag reveal">Our Capabilities</span>
          <h1 className="section-title reveal delay-100">
            Services that <span>Scale Your Business</span>
          </h1>
          <p className="section-subtitle reveal delay-200">
            From AI-first products to enterprise cloud platforms, VistaraKlyQs delivers full-lifecycle
            software services powered by the latest technology.
          </p>
        </div>
      </section>

      {/* All Services */}
      <section className="all-services-section">
        <div className="section-wrapper">
          {allServices.map((s, i) => (
            <div
              key={i}
              className={`service-detail-card glass-card reveal ${i % 2 === 0 ? '' : 'reverse'}`}
              style={{ '--accent-color': s.color }}
            >
              <div className="sd-icon-wrap">
                <div className="sd-icon">{s.icon}</div>
              </div>
              <div className="sd-content">
                <h2 className="sd-title">{s.title}</h2>
                <p className="sd-short">{s.short}</p>
                <p className="sd-desc">{s.desc}</p>
                <ul className="sd-features">
                  {s.features.map((f) => (
                    <li key={f}>
                      <span className="feature-check">✓</span> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="btn-outline">
                  Get a Quote →
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Cloud */}
      <section className="cloud-section">
        <div className="section-wrapper">
          <div className="section-header reveal">
            <span className="section-tag">Cloud & Infrastructure</span>
            <h2 className="section-title">Multi-Cloud <span>Expertise</span></h2>
          </div>
          <div className="cloud-grid">
            {cloudServices.map((c, i) => (
              <div key={i} className={`cloud-card glass-card reveal delay-${i * 200}`}>
                <div className="cloud-icon">{c.icon}</div>
                <h3>{c.name}</h3>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DevOps */}
      <section className="devops-section">
        <div className="section-wrapper">
          <div className="section-header reveal">
            <span className="section-tag">DevOps & Automation</span>
            <h2 className="section-title">Ship Faster, <span>Ship Smarter</span></h2>
          </div>
          <div className="devops-grid">
            {devOps.map((d, i) => (
              <div key={i} className={`devops-card glass-card reveal delay-${i * 100 + 100}`}>
                <h4 className="devops-name">{d.name}</h4>
                <p className="devops-desc">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="services-cta">
        <div className="section-wrapper services-cta-inner reveal">
          <h2 className="cta-title">Let's build your next <span>big idea</span></h2>
          <p className="cta-sub">Tell us what you need. We'll put together a tailored proposal within 48 hours.</p>
          <Link to="/contact" className="btn-primary">Contact Our Team</Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
